<template>
  <DataPage
    ref="usersDataPage"
    :columns="columns"
    :filters="filters"
    :fetch-callback="fetchUsers"
    :model="UserModel"
    :title="__('entities.related-users')"
    has-row-actions
    :selectable="canSendRegistrationEmails"
    :row-selected="handleRowSelect"
  >
    <template #pageHeader>
      <EntityShowHeader
        :entity="entity"
        :selected-rows="selectedRows"
        :can-send-registration-emails="canSendRegistrationEmails"
        @sendRegistrationEmailsClick="sendRegistrationEmails"
      />
    </template>

    <template #tableHeaderActions>
      <div class="d-flex align-items-center">
        <b-button
          v-can:entities.update
          v-can:users.create
          size="sm"
          variant="outline-primary"
          data-testid="create-user-btn"
          @click="openEntityUserCreateModal"
        >
          <Icon icon="plus-circle" size="14" class="mr-2" />
          {{ __('common.create-user') }}
        </b-button>
      </div>
    </template>

    <template #name="{ item }">
      <RouterLink
        v-if="authUser && item.id === authUser.id"
        :to="{ name: 'profile.show' }"
      >
        {{ item.name }}
      </RouterLink>
      <RouterLink
        v-else
        v-can:users.view
        :to="{ name: 'users.show', params: { userId: item.id } }"
      >
        {{ item.name }}
      </RouterLink>
    </template>

    <template #status="{ item }">
      <Badge :variant="item.statusData().badge">
        {{ item.statusData().label }}
      </Badge>
    </template>

    <template #role="{ item }">
      <span v-for="(role, index) in item.roles" :key="index" class="mr-1">
        <Badge variant="primary">
          {{ role.name }}
        </Badge>
      </span>
    </template>

    <template #entity="{ item }">
      <RouterLink
        :to="{
          name: 'entities.show',
          params: {
            entityId: item.defaultEntity?.id ?? item.defaultEntityId,
          },
        }"
      >
        {{ entityName(item) }}
      </RouterLink>
    </template>

    <template #actionButtons="{ item }">
      <ActionPopover v-if="authUser && item.id === authUser.id" :id="item.id">
        <b-button
          v-can:users.view
          class="dropdown-item"
          size="sm"
          :to="{ name: 'profile.show' }"
        >
          <Icon icon="eye" class="mr-2" size="14" />
          {{ __('buttons.show') }}
        </b-button>
      </ActionPopover>
      <ActionPopover
        v-else
        :id="item.id"
        v-can-any="['users.view', 'users.update']"
      >
        <b-button
          v-can:users.view
          class="dropdown-item"
          size="sm"
          :to="{ name: 'users.show', params: { userId: item.id } }"
        >
          <Icon icon="eye" class="mr-2" size="14" />
          {{ __('buttons.show') }}
        </b-button>

        <b-button
          v-can:users.update
          class="dropdown-item"
          size="sm"
          @click="handleEditUser(item)"
        >
          <Icon icon="edit" class="mr-2" size="14" />
          {{ __('buttons.edit') }}
        </b-button>

        <b-button
          v-can:users.email.registration
          class="dropdown-item"
          size="sm"
          @click="sendRegistrationEmail(item)"
        >
          <Icon icon="envelope-send" class="mr-2" size="14" />
          {{ __('users.send-registration-email') }}
        </b-button>
      </ActionPopover>
    </template>
  </DataPage>
</template>

<script>
import LazyLoadComponent from '@/General/LazyLoadComponent.js'
import DataPage from '@/General/Tables/DataPage.vue'
import UserModel from '@/Modules/User/UserModel.js'
import EntityUserService from '@/Modules/Entity/EntityUser/EntityUserService.js'
import EntityService from '@/Modules/Entity/EntityService.js'
import EntityShowHeader from '@/Modules/Entity/Components/EntityShowHeader.vue'
import FilterManager, {
  FILTER_TAB,
  OPERATOR_ACTION,
} from '@/General/Tables/FilterManager.js'
import EntityUserCreateModal from '@/Modules/Entity/EntityUser/Components/EntityUserCreateModal.vue'
import CreateUserModal from '@/Modules/User/Components/CreateUserModal.vue'
import UserService from '@/Modules/User/UserService.js'
import { AUTH_USER_GETTER } from '@/Modules/Auth/AuthModule.js'
import { mapGetters } from 'vuex'
import store from '@/Setup/SetupStore.js'

export default {
  name: 'List',

  components: { EntityShowHeader, DataPage },

  extends: LazyLoadComponent((to, callback) => {
    EntityService.show(to.params.entityId).then((entity) => {
      callback(function () {
        this.entity = entity
      })
    })
  }),

  data() {
    return {
      UserModel: UserModel,
      entity: {},
      filters: [
        {
          type: FILTER_TAB,
          operator: OPERATOR_ACTION,
          options: [
            {
              value: 'direct',
              label: this.__(`entities.direct`),
              default: true,
            },
            {
              value: 'direct_and_subsidiaries',
              label: this.__(`entities.direct-subsidiaries`),
            },
          ],
        },
      ],
      columns: [
        {
          column: 'name',
          title: this.__(`common.name`),
          custom: true,
        },
        {
          column: 'email',
          title: this.__(`common.email`),
        },
        {
          column: 'entity',
          title: this.__(`common.main_entity`),
          custom: true,
        },
        {
          column: 'role',
          title: this.__(`menu.roles`),
          custom: true,
        },
        {
          column: 'status',
          title: this.__(`common.status`),
          custom: true,
        },
      ],
      selectedRows: [],
    }
  },

  computed: {
    ...mapGetters('AuthModule', {
      authUser: AUTH_USER_GETTER,
    }),

    canSendRegistrationEmails() {
      const user = store.getters['AuthModule/' + AUTH_USER_GETTER]
      return user && user.permissions.includes('users.email.registration')
    },
  },

  methods: {
    fetchUsers(parameters) {
      parameters = this.assignDefaultFilterIfNoFilters(parameters)
      return EntityUserService.list(this.$route.params.entityId, parameters)
    },

    assignDefaultFilterIfNoFilters(parameters) {
      if (parameters.filters) return parameters
      const filter = this.filters[0]
      const defaultOption = filter.options.find((option) => option.default)
      const filters = FilterManager.setFilter(
        parameters.filters,
        filter,
        defaultOption.value
      )
      parameters.filters = filters
      return parameters
    },

    entityName(item) {
      try {
        if (item.defaultEntity?.name) {
          return item.defaultEntity?.name
        }
        if (item.entity?.id === item.defaultEntityId) {
          return item.entity?.name
        }
      } catch (error) {
        console.error(error)
      }
    },

    openEntityUserCreateModal() {
      this.showModal({
        component: EntityUserCreateModal,
        label: this.__(`common.create-user`),
        icon: 'plus-circle',
        class: 'modal-lg',
        meta: {
          entityId: this.$route.params.entityId,
        },
        onSuccess: () => {
          this.$refs.usersDataPage.refreshTable()
          this.$successToast(this.__(`users.create-success`))
        },
      })
    },

    async handleEditUser(item) {
      if (!item?.id) {
        return
      }
      try {
        const data = await UserService.show(item.id)
        const { user } = data
        this.showModal({
          component: CreateUserModal,
          label: this.__(`common.edit-user`),
          icon: 'user',
          class: 'modal-md',
          showExpand: true,
          onSuccess: () => {
            this.$refs.usersDataPage.refreshTable()
          },
          meta: {
            user,
          },
        })
      } catch (error) {
        console.error(error)
      }
    },

    handleRowSelect(rows) {
      this.selectedRows = [...rows]
    },

    sendRegistrationEmail(user) {
      if (!user) {
        return
      }
      this.$showConfirmationModal(
        this.__('users.send-registration-emails-confirm', { count: 1 }),
        () => {
          UserService.sendRegistrationEmails({
            userIds: [user.id],
          })
            .then(() => {
              this.$refs.usersDataPage.refreshTable()
              this.$successToast(
                this.__('users.send-registration-email-success')
              )
            })
            .catch((error) => this.$errorToast(error.response.data.message))
        }
      )
    },

    sendRegistrationEmails() {
      this.$showConfirmationModal(
        this.__('users.send-registration-emails-confirm', {
          count: this.selectedRows.length,
        }),
        () => {
          UserService.sendRegistrationEmails({
            userIds: this.selectedRows.map((user) => user.id),
          })
            .then(() => {
              this.$refs.usersDataPage.refreshTable()
              this.$successToast(
                this.__('users.send-registration-emails-success')
              )
            })
            .catch((error) => this.$errorToast(error.response.data.message))
        }
      )
    },
  },
}
</script>
